import React, { useState } from 'react';
import Modal from '@atlaskit/modal-dialog';

import ChartColorsProvider from 'contextProviders/ChartColorsProvider';
import ComparisonContext from 'contexts/ComparisonContext';
import DateInputProvider from 'contextProviders/DateInputProvider';
import FilterPlatesProvider from 'contextProviders/FilterPlatesProvider';
import useDashboardGadgetDataTypes from 'hooks/useDashboardGadgetDataTypes';
import { SortToggleProvider } from 'components/V5GadgetForm/SortToggleContext';
import { InsightsChartSpec } from 'services/streamInsightsQuery';

import buildChartDefFromSpec from '../saveChart/buildChartDefFromSpec';
import buildScopeFromSource from '../saveChart/buildScopeFromSource';
import ExploreChartContent from './ExploreChartContent';
import ExploreDataTypesSync from './ExploreDataTypesSync';

// Opens one of the agent's chat charts as the LIVE gadget, so the user can
// interrogate it instead of staring at a snapshot.
//
// The chat chart is a static picture of an aggregate_metric result. Here we
// rebuild the same chart as a real V5ChartDefinition and hand it the provider
// stack a dashboard would — so it refetches, responds to a date change, and
// filters when you click a bar. Nothing is persisted; the caller unmounts this
// on close, which resets every bit of state below.
//
// Deliberately NOT mounted: DashboardContextMenuProvider. The Highcharts click
// handlers branch on `openMenu` — present, they open the "view raw data" menu;
// absent, they call onDrillDown and filter in place. In-place drilling is the
// point of Explore, and raw data is still on the gadget's kebab.
const ExploreChartModal = ({
  spec,
  onClose,
}: {
  spec: InsightsChartSpec;
  onClose: () => void;
}) => {
  // Widened to VisualisationDefinition because the config rail's onChange is
  // typed for every gadget family, not just V5.
  const [chartDef, setChartDef] = useState<VisualisationDefinition | undefined>(
    () => buildChartDefFromSpec(spec),
  );
  // The agent's filters are the chart's SCOPE, not drill-downs. As drill-downs
  // they were displayed but silently dropped from the query: useFilterInput
  // slices off the last drill-down when its field matches the chart's grouping
  // dimension (right on a dashboard, where a self-filter would collapse the
  // card to one bar), and an agent filter on the broken-down field is exactly
  // that case — hence ENG-1023's "filtered for 10 drivers, plots 543". Scope is
  // never suppressed, is what the save path already persists, and its plates
  // are still removable here (FilterPlate treats scope as interactive), so
  // nothing is lost.
  const [scope, setScope] = useState<FilterPlateType[]>(() =>
    buildScopeFromSource(spec.source.filters),
  );
  // Drill-downs stay empty until the user clicks into the chart.
  const [drillDowns, setDrillDowns] = useState<FilterPlateType[]>([]);
  const [currentComparison, setCurrentComparison] = useState<
    PersistedComparisonType | undefined
  >();

  const dataTypes = useDashboardGadgetDataTypes(undefined, chartDef);

  return (
    <Modal
      width="95vw"
      height="80vh"
      onClose={onClose}
      shouldScrollInViewport={false}
      autoFocus={false}
    >
      <ChartColorsProvider>
        <DateInputProvider
          initialDateRange={{
            startDate: spec.source.dateScope.startDate,
            endDate: spec.source.dateScope.endDate,
          }}
          initialDateField="date"
        >
          <ComparisonContext.Provider
            value={{ currentComparison, setCurrentComparison }}
          >
            <FilterPlatesProvider
              drillDowns={drillDowns}
              setDrillDowns={setDrillDowns}
              scope={scope}
              setScope={setScope}
              dataTypes={dataTypes}
            >
              <SortToggleProvider>
                <>
                  <ExploreDataTypesSync dataTypes={dataTypes} />
                  <ExploreChartContent
                    spec={spec}
                    chartDef={chartDef}
                    setChartDef={setChartDef}
                    onClose={onClose}
                  />
                </>
              </SortToggleProvider>
            </FilterPlatesProvider>
          </ComparisonContext.Provider>
        </DateInputProvider>
      </ChartColorsProvider>
    </Modal>
  );
};

export default ExploreChartModal;
